import { API_BASE_URL } from './env';
import { ApiError, NetworkError, type ApiErrorBody } from './errors';
import { tokenStore } from './token-store';

/**
 * The one way this app talks to the backend.
 *
 * Every route answers with `{ data }`, `{ data, page }`, or 204 and nothing —
 * so unwrapping happens here rather than in three API modules that would each
 * have to remember to do it.
 */

export type Page<T> = {
  data: T[];
  page: { limit: number; offset: number; count: number; hasMore: boolean };
};

type QueryValue = string | number | boolean | null | undefined;

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Multipart, for the one route that takes a file. Set instead of `body`. */
  form?: FormData;
  /** Skips the bearer header and the refresh retry — used by the auth routes. */
  anonymous?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
};

/**
 * How long to wait before deciding nobody is answering.
 *
 * `fetch` on a dead line does not reject — it hangs, sometimes until the tab is
 * closed. Without a ceiling a rider who taps "I've collected the bag" in a
 * basement stockroom would watch a spinner rather than get an error they can
 * retry, and would have no idea whether the tap landed.
 *
 * Longer than the customer app's fifteen seconds, because the connection here
 * is worse by definition: this is used while moving, indoors, on mobile data.
 */
const REQUEST_TIMEOUT = 20_000;

/**
 * Called when the session is beyond saving, so the app can drop to signed-out
 * rather than showing a wall of 401s. Wired up by the auth provider.
 */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const envelope = (body as Partial<ApiErrorBody> | null)?.error;
  return new ApiError(
    response.status,
    envelope?.code ?? `HTTP_${response.status}`,
    envelope?.message ?? response.statusText ?? 'Request failed',
    envelope?.details,
  );
}

/**
 * Three outcomes, not two, and the difference matters.
 *
 * `rejected` means the server answered and said no: the session really is over
 * and the tokens are cleared. `unreachable` means nobody answered — the session
 * is probably fine, the line is not, and clearing the tokens would sign a rider
 * out mid-delivery because they walked into a lift.
 */
type RefreshOutcome = 'refreshed' | 'rejected' | 'unreachable';

/*
 * One refresh in flight at a time.
 *
 * The run sheet, the profile and a poll can all fire at once, and without this
 * a single expiry would spend several refresh tokens on the same rotation. The
 * backend treats a re-presented refresh token as theft and revokes the whole
 * family, so the losers of that race would not merely retry — they would be
 * signed out, on a bike, holding somebody's medicine.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

function refreshSession(): Promise<RefreshOutcome> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return Promise.resolve('rejected');

  refreshInFlight ??= (async (): Promise<RefreshOutcome> => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        // Bounded like everything else. This is the request every other
        // request funnels through on a 401; unbounded, it hangs them all.
        signal: AbortSignal.timeout(REQUEST_TIMEOUT),
      });
      if (!response.ok) {
        tokenStore.clear();
        return 'rejected';
      }
      const payload = (await response.json()) as {
        data: { accessToken: string; refreshToken: string };
      };
      tokenStore.set(payload.data);
      return 'refreshed';
    } catch {
      // Tokens deliberately left alone. See RefreshOutcome.
      return 'unreachable';
    } finally {
      // Cleared in a microtask so every awaiter of this attempt sees one result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/** Resolves when a signal aborts, so a deadline can be raced against a promise. */
function whenAborted(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    signal.addEventListener('abort', () => resolve(), { once: true });
  });
}

async function send(
  path: string,
  options: RequestOptions,
  deadline: AbortSignal,
): Promise<Response> {
  const headers = new Headers();
  // Multipart sets its own content-type, with the boundary the browser
  // generated — one written by hand here would not match it.
  if (options.body !== undefined) headers.set('content-type', 'application/json');

  const token = tokenStore.getAccessToken();
  if (!options.anonymous && token) headers.set('authorization', `Bearer ${token}`);

  const signal = options.signal
    ? (AbortSignal.any?.([options.signal, deadline]) ?? options.signal)
    : deadline;

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
      signal,
    });
  } catch (error) {
    // A caller who cancelled on purpose gets their own abort back.
    if (options.signal?.aborted) throw error;
    throw new NetworkError(
      deadline.aborted
        ? 'The server took too long to answer. Check your signal and try again.'
        : undefined,
    );
  }
}

/** Sends, refreshes once on a 401, and turns any non-2xx into an ApiError. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  /*
   * ONE deadline for the whole call, not one per round trip. A 401 turns a
   * single call into three — request, refresh, retry — and arming a fresh
   * timer for each would let a caller who asked to wait 20s be held for 60.
   */
  const deadline = AbortSignal.timeout(options.timeoutMs ?? REQUEST_TIMEOUT);

  let response = await send(path, options, deadline);

  if (response.status === 401 && !options.anonymous) {
    const outcome = await Promise.race([
      refreshSession(),
      whenAborted(deadline).then((): RefreshOutcome => 'unreachable'),
    ]);

    if (outcome === 'unreachable') {
      throw new NetworkError('The server took too long to answer. Check your signal.');
    }
    if (outcome === 'refreshed') {
      response = await send(path, options, deadline);
    } else {
      // The server said no. Nothing to retry, and nothing to revoke.
      onSessionExpired?.();
    }
  }

  if (!response.ok) throw await toApiError(response);

  // 204 No Content — the location ping, and logout.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** For the `{ data: T }` envelope, which is most routes. */
export async function requestData<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const payload = await request<{ data: T }>(path, options);
  return payload.data;
}

/** For the `{ data: T[], page }` envelope. */
export function requestPage<T>(path: string, options: RequestOptions = {}): Promise<Page<T>> {
  return request<Page<T>>(path, options);
}

/**
 * Makes a URL the API handed back absolute, if it is not already.
 *
 * The local storage driver returns file links as ROOT-RELATIVE paths, because
 * the front ends are served through a proxy and are same-origin with the API.
 * An already-absolute URL passes through untouched, which is what keeps this
 * correct when storage moves to S3 and starts presigning real links.
 */
export function absoluteApiUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return new URL(url, window.location.origin).toString();
}
